import { usePatient } from '@hook'
import { FormButton, Modal } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { createOption, getError, showToast } from '@util'
import { useMemo } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { useProductModules } from '../../../../modules/useProductModules'

type Props = {
  id?: string
  onClose: () => void
}

const options = ['Yes', 'No', 'N/A']?.map(item => createOption(item))

const ChoiceSelect = ({ name, label }: { name: string; label: string }) => {
  const { control, watch } = useFormContext()

  const formVal = watch(name)

  const updatedOptions = useMemo(() => {
    if (formVal) return options.some(({ value }) => value === formVal) ? options : [createOption(formVal), ...options]

    return options
  }, [formVal])

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => (
        <Select
          borderRadius={'100px'}
          getOptionValue={({ value }) => value}
          isCreatable
          label={label}
          onChange={(val: any) => onChange(val?.value)}
          options={updatedOptions}
          placeholder="select or type in your value"
          value={updatedOptions.find(({ value: val }) => val === value)}
        />
      )}
    />
  )
}

export const Choice = ({ onClose, id }: Props) => {
  const { updatePatient, singlePatient } = usePatient({ id })
  const { isModuleEnabled } = useProductModules()
  const portalEnabled = isModuleEnabled('patient_portal')
  const methods = useForm({ defaultValues: singlePatient?.attributes?.choices })
  const { handleSubmit, formState } = methods

  const onSubmit = async (formVal: any) => {
    const choices = { ...formVal }
    if (!portalEnabled) {
      delete choices.allow_patient_portal
      delete choices.cms_portal_login
    }
    const payload = {
      data: {
        attributes: {
          choices
        },
        type: 'patient'
      }
    }
    try {
      await updatePatient({ formVal: payload, id: id || '' })
      showToast('Patient updated successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Edit Chioces">
        <form
          className="flex w-full max-w-[min(890px,calc(100vw-1.5rem))] flex-col overflow-auto"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="grid md:grid-cols-2 grid-cols-1 gap-x-5 gap-y-8 p-6 overflow-auto">
            <ChoiceSelect label="Allow Health Information Exchange" name="allow_health_information_exchange" />
            <ChoiceSelect label="Allow Email" name="allow_email" />
            <ChoiceSelect label="Allow Immunization Registry Use" name="allow_immunization_registry_use" />
            <ChoiceSelect label="Allow Immunization Info Sharing" name="allow_immunization_info_sharing" />
            <ChoiceSelect label="Allow SMS" name="allow_sms" />
            <ChoiceSelect label="Core Team (Provider)" name="care_team_provider" />
            {portalEnabled ? <ChoiceSelect label="Allow Patient Portal" name="allow_patient_portal" /> : null}
            <ChoiceSelect label="Core Team Status" name="care_team_status" />
            <ChoiceSelect label="Allow Voice Message" name="allow_voice_message" />
            <ChoiceSelect label="HiIPPAA Notice  Received" name="hipaa_notice_received" />
            {portalEnabled ? <ChoiceSelect label="CMS Portal Login" name="cms_portal_login" /> : null}
            <ChoiceSelect label="Immunization Registration Status " name="immunization_registry_status" />
            <ChoiceSelect label="Care Team Facility" name="care_team_facility" />
            <ChoiceSelect label="Leave Message with" name="leave_message_with" />
            <ChoiceSelect
              label="Immunization Registration Status Effective Date"
              name="immunization_registry_status_effective_date"
            />
            <ChoiceSelect label="Prevent APIAccess" name="prevent_api_access" />
            <ChoiceSelect label="Patient Categories" name="patient_categories" />
            <ChoiceSelect label="Protection Indicator" name="protection_indicator" />
            <ChoiceSelect label="Protection Indicator Effective Date" name="protection_indicator_effective_date" />
            <ChoiceSelect label="Provider" name="provider" />
            <ChoiceSelect label="Provider Since Date" name="provide_since_date" />
            <ChoiceSelect label="Publicity Code" name="publicity_code" />
            <ChoiceSelect label="Publicity Code Effective Date" name="publicity_code_effective_date" />
            <ChoiceSelect label="Referring Provider" name="referring_provider" />
          </div>
          <FormButton isLoading={formState.isSubmitting} onClose={onClose} submit="Save" />
        </form>
      </Modal>
    </FormProvider>
  )
}
